import { List, Menu } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import * as coreApi from "api/Core";
import { FilterBarStyleClasses, GlobalSearchStyleClasses } from "assets/jss/common-styles";
import ButtonOrderBy from "components/Buttons/ButtonOrderBy";
import { SpinnerIndicator } from "components/CallServer";
import FilterCountInfo from "components/FilterCountInfo";
import { MuiFilterAppBar, MuiFilterLine, MuiFilterToolbar } from "components/MuiExtras/Filter";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiMenuItemChecked from "components/MuiExtras/MuiMenuItemChecked";
import MuiPagination from "components/MuiExtras/MuiPagination";
import MuiPaper from "components/MuiExtras/MuiPaper";
import MuiPushRight from "components/MuiExtras/MuiPushRight";
import AppResource from "components/Translations/AppResources";
import { GlobalDocumentSearchFilter, GlobalSearchDocumentOrderBy, GlobalSearchRecord } from "models/ApiModels";
import queryString from 'query-string';
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router";
import { GlobalSearchRowsPerPage } from "views/Search/FullGlobalSearch";
import { SearchRenderDocument } from "./Components/SearchRenderDocument";

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    dialog: any;
    loading: boolean;
    searchResult: GlobalSearchRecord;
    recordsLoaded: number;
    lastOffset: number;
    orderByMenuAnchorEl: any;
    lastError: any;
}

interface IProps {
}

type Props = IProps & RouteComponentProps<{}> & WithStyles<typeof FilterBarStyleClasses> & WithStyles<typeof GlobalSearchStyleClasses> & WithTranslation;

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export class FullDocumentSearch extends React.Component<Props, IState> {
    private filter: GlobalDocumentSearchFilter;
    private orderBy: GlobalSearchDocumentOrderBy = GlobalSearchDocumentOrderBy.MostRelevant;

    constructor(props: Props) {
        super(props);
        this.state = {
            dialog: null,
            searchResult: null,
            recordsLoaded: 0,
            lastOffset: 0,
            orderByMenuAnchorEl: null,
            loading: true,
            lastError: null,
        };
        this.clearFilter(props);
    }


    //#region FILTER

    private renderAdvFilter(): JSX.Element {
        return (
            <MuiFilterAppBar position="static">
                <MuiFilterToolbar variant="dense" >
                    <MuiFilterLine>
                        <MuiPushRight>
                            <ButtonOrderBy
                                text={this.getOrderByName(this.orderBy)}
                                onClick={(e) => { this.setState({ orderByMenuAnchorEl: e.currentTarget }); }}
                            />

                            <Menu
                                id="simple-menu"
                                anchorEl={this.state.orderByMenuAnchorEl}
                                open={this.state.orderByMenuAnchorEl != null}
                                onClose={() => { this.setState({ orderByMenuAnchorEl: null }); }}
                            >
                                <MuiMenuItemChecked text={this.getOrderByName(GlobalSearchDocumentOrderBy.MostRelevant)} value={GlobalSearchDocumentOrderBy.MostRelevant} currentSelection={this.orderBy} onClick={this.applyOrder} />
                                <MuiMenuItemChecked text={this.getOrderByName(GlobalSearchDocumentOrderBy.LastUpdateDate)} value={GlobalSearchDocumentOrderBy.LastUpdateDate} currentSelection={this.orderBy} onClick={this.applyOrder} />
                                <MuiMenuItemChecked text={this.getOrderByName(GlobalSearchDocumentOrderBy.Title)} value={GlobalSearchDocumentOrderBy.Title} currentSelection={this.orderBy} onClick={this.applyOrder} />
                            </Menu>
                        </MuiPushRight>
                    </MuiFilterLine>
                </MuiFilterToolbar>
            </MuiFilterAppBar>
        );
    }

    private getOrderByName(o: GlobalSearchDocumentOrderBy): string {
        switch (o) {
            case GlobalSearchDocumentOrderBy.MostRelevant: return this.props.t("Common.Filter.Toolbar.OrderBy.MostRelevant");
            case GlobalSearchDocumentOrderBy.LastUpdateDate: return this.props.t("Common.Filter.Toolbar.OrderBy.LastModified");
            case GlobalSearchDocumentOrderBy.Title: return this.props.t("Common.Filter.Toolbar.OrderBy.Title");
        }
        return "?";
    }

    private reset(p: Props) {
        this.clearFilter(p);
        this.loadDocuments(0);
    }

    private clearFilter(p: Props) {
        const values = queryString.parse(p.location.search);
        var searchText = decodeURIComponent(values.q as string);
        this.filter = {
            searchText: searchText,
            publicationType: null,
        };
    }

    private applyOrder = (orderBy: GlobalSearchDocumentOrderBy) => {
        this.orderBy = orderBy;
        this.loadDocuments(0);
    };

    //#endregion


    private loadDocuments(offset: number) {

        this.setState({
            loading: true,
            lastError: null,
            orderByMenuAnchorEl: null,
        });

        coreApi.globalSearchDocuments(
            this.filter,
            this.orderBy,
            offset,
            GlobalSearchRowsPerPage
        )
            .then((result) => {
                this.setState({
                    loading: false,
                    searchResult: result,
                    lastOffset: offset
                });
            })
            .catch((lastError) => {
                this.setState({ lastError: lastError.message || lastError, loading: false });
            });
    }


    private renderResult() {
        const { searchResult, lastOffset } = this.state;

        if (searchResult.totalRecords == 0) return null;

        return (
            <MuiContainer>
                <MuiPaper paperColor="white" className={this.props.classes.documentCard}>
                    <List>
                        {
                            searchResult.items.map((v, index) => {
                                return <SearchRenderDocument data={v} index={index} key={index} showDivider={false} querySearch={this.filter.searchText} />;
                            })
                        }
                    </List>
                    {
                        searchResult.totalRecords > GlobalSearchRowsPerPage &&
                        <MuiPagination
                            limit={GlobalSearchRowsPerPage}
                            offset={lastOffset}
                            total={searchResult.totalRecords}
                            onClick={(e, offset) => this.loadDocuments(offset)}
                        />
                    }
                </MuiPaper>
            </MuiContainer>
        );
    }

    private renderSearch() {

        const { searchResult, loading } = this.state;

        if (loading)
            return <SpinnerIndicator />;

        return (
            <>
                {
                    searchResult.totalRecords > 0
                        ? (
                            <>
                                <MuiContainer margin="normal">
                                    <FilterCountInfo
                                        count={searchResult.totalRecords}
                                        additionalInfo={`${this.props.t("Glossary.To")} "${this.filter.searchText}"`}
                                    />
                                </MuiContainer>
                                {this.renderResult()}
                            </>
                        )
                        : <MuiMessageBox variant="info" message={<AppResource resource="Views.Search.FullGlobalSearch.NoSearchResults" />} />
                }
            </>
        );
    }

    /////////////////////////////////////////////////////////////////////////////////////////////////

    public componentDidMount() {
        this.loadDocuments(0);
    }

    public componentDidUpdate(prevProps: Props) {
        if (prevProps.location.search != this.props.location.search)
            this.reset(this.props);
    }

    public render() {
        const { dialog, lastError } = this.state;

        return (
            <div >
                {dialog}

                {this.renderAdvFilter()}

                {
                    lastError != null
                        ? <MuiMessageBox variant="error" message={lastError} />
                        : this.renderSearch()
                }
            </div>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

const withAllStyles = withStyles(
    (theme) => ({
        ...FilterBarStyleClasses(theme),
        ...GlobalSearchStyleClasses(theme),
    }),
    { withTheme: true },
);


export default withTranslation()(withAllStyles(FullDocumentSearch));
